import type { SetDistribution } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, toast } from '../../ui/feedback';
import { Checkbox } from '../../ui/form';
import { Panel } from '../../ui/layout';

/**
 * Рассылка ревизии с отметкой прочтения (ПД-11) на карточке комплекта: плашка «Ознакомьтесь» получателю
 * и блок «Рассылка» тому, кто рассылает — кто прочитал, «Напомнить», «Разослать».
 */

type People = Array<{ id: string; fullName: string; external: boolean }>;

export function ReadBanner({ setId }: { setId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['psd-distribution', setId],
    queryFn: () => api<SetDistribution>(`/psd/sets/${setId}/distribution`),
    retry: false,
  });
  const m = useMutation({
    mutationFn: (revisionId: string) => api(`/psd/revisions/${revisionId}/read`, { body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['psd-distribution', setId] });
      toast('Отмечено: ознакомлен');
    },
  });
  const unread = (q.data?.mine ?? []).filter((x) => !x.readAt);
  if (!unread.length) return null;
  return (
    <div className="mb-4 flex flex-col gap-2 rounded-md bg-warning-soft px-4 py-3 text-[14px] md:flex-row md:items-center">
      <span className="flex-1">
        Вам разослана ревизия {unread.map((x) => x.revision).join(', ')} — ознакомьтесь с листами. Открытие
        файла тоже отмечает прочтение.
      </span>
      {unread.map((x) => (
        <Button
          key={x.revisionId}
          variant="primary"
          className="h-11 md:h-10"
          loading={m.isPending && m.variables === x.revisionId}
          onClick={() => m.mutate(x.revisionId)}
        >
          Ознакомлен с {x.revision}
        </Button>
      ))}
      <ErrorBox error={m.error} />
    </div>
  );
}

export function DistributionPanel({ setId, projectId }: { setId: string; projectId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['psd-distribution', setId],
    queryFn: () => api<SetDistribution>(`/psd/sets/${setId}/distribution`),
    retry: false,
  });
  const [send, setSend] = useState<string | null>(null);
  const remind = useMutation({
    mutationFn: (revisionId: string) =>
      api<{ reminded: number; external: number }>(`/psd/revisions/${revisionId}/distribution/remind`, {
        body: {},
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['psd-distribution', setId] });
      toast(
        r.reminded ? `Напомнили: ${r.reminded}` : 'Напоминать пока некому — или напоминали меньше часа назад',
      );
    },
  });
  if (!q.data?.canSend) return null;
  const list = q.data.revisions;
  return (
    <Panel title="Рассылка">
      {list.length ? (
        <div className="flex flex-col gap-4">
          {list.map((r) => {
            const unread = r.rows.filter((x) => !x.readAt).length;
            return (
              <div key={r.revisionId} className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2 text-[14px]">
                  <span className="num font-medium">{r.revision}</span>
                  {r.rows.length ? (
                    <span className="text-ink-2">
                      ознакомились {r.rows.length - unread} из {r.rows.length}
                    </span>
                  ) : (
                    <span className="text-ink-2">не рассылалась</span>
                  )}
                </div>
                {r.rows.length ? (
                  <ul className="flex flex-col gap-1 text-[14px]">
                    {r.rows.map((x) => (
                      <li key={x.userId} className="flex flex-wrap items-center gap-1.5">
                        <span className="[overflow-wrap:anywhere]">
                          {x.fullName}
                          {x.external ? ' (внешний)' : ''}
                        </span>
                        {x.readAt ? (
                          <Badge tone="success">ознакомлен {dateTime(x.readAt)}</Badge>
                        ) : (
                          <Badge tone="warning">
                            не прочитал{x.reminders ? ` · напоминаний ${x.reminders}` : ''}
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {r.status === 'production' ? (
                    <Button className="h-11 md:h-9" onClick={() => setSend(r.revisionId)}>
                      Разослать
                    </Button>
                  ) : null}
                  {unread ? (
                    <Button
                      variant="ghost"
                      className="h-11 md:h-9"
                      loading={remind.isPending}
                      onClick={() => remind.mutate(r.revisionId)}
                    >
                      Напомнить ({unread})
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
          <ErrorBox error={remind.error} />
        </div>
      ) : (
        <p className="text-[14px] text-ink-2">Разослать можно ревизию «В производство работ».</p>
      )}
      {send ? (
        <SendDialog
          revisionId={send}
          projectId={projectId}
          already={list.find((r) => r.revisionId === send)?.rows.map((x) => x.userId) ?? []}
          onClose={() => setSend(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ['psd-distribution', setId] })}
        />
      ) : null}
    </Panel>
  );
}

function SendDialog({
  revisionId,
  projectId,
  already,
  onClose,
  onDone,
}: {
  revisionId: string;
  projectId: string;
  already: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const people = useQuery({
    queryKey: ['psd-people', projectId],
    queryFn: () => api<People>(`/psd/projects/${projectId}/people`),
  });
  const [ids, setIds] = useState<string[]>([]);
  const m = useMutation({
    mutationFn: () =>
      api<{ added: number }>(`/psd/revisions/${revisionId}/distribution`, { body: { userIds: ids } }),
    onSuccess: (r) => {
      onDone();
      toast(`Разослано: ${r.added}`);
      onClose();
    },
  });
  const list = (people.data ?? []).filter((p) => !already.includes(p.id));
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Разослать ревизию"
      description="Сотрудники получат поручение «ознакомиться» в «Ждёт меня», внешние — отметку на карточке комплекта."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!ids.length}
            onClick={() => m.mutate()}
          >
            Разослать ({ids.length})
          </Button>
        </>
      }
    >
      {list.length ? (
        <div className="flex flex-col gap-1">
          <Checkbox
            className="min-h-11 items-center md:min-h-0"
            checked={ids.length === list.length}
            onChange={(e) => setIds(e.target.checked ? list.map((p) => p.id) : [])}
            label="Всем участникам проекта с правом на ПСД"
          />
          <div className="grid max-h-72 gap-1 overflow-auto rounded-md border border-line p-2">
            {list.map((p) => (
              <Checkbox
                key={p.id}
                className="min-h-11 items-center md:min-h-0"
                checked={ids.includes(p.id)}
                onChange={(e) =>
                  setIds((x) => (e.target.checked ? [...x, p.id] : x.filter((y) => y !== p.id)))
                }
                label={`${p.fullName}${p.external ? ' (внешний)' : ''}`}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="text-[14px] text-ink-2">Всем участникам с правом на ПСД уже разослано.</p>
      )}
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}
